import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Star } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { catalogueReducer } from '../../store/catalogue/catalogue.reducer';
import { getCatalogueIsLoading, getCatalogueManufacturers } from '../../store/catalogue/catalogue.selectors';
import { userReducer } from '../../store/reducers/user.reducer';
import { getUser } from '../../store/selectors/user.selector';
import { resolveImageSrc } from '../../utils/imageUtils';
import { LoadingSpinner } from '../loading/LoadingSpinner';

const { selectManufacturer } = catalogueReducer.actions;
const { updateUser } = userReducer.actions;

const ManufacturerGrid: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const manufacturers = useAppSelector(getCatalogueManufacturers);
  const isLoading = useAppSelector(getCatalogueIsLoading);
  const user = useAppSelector(getUser);
  const favoriteIds = new Set(user?.favoriteManufacturerIds ?? []);

  const location = useLocation();
  const favoritesOnly = location.hash === '#favorites';

  const visibleManufacturers = favoritesOnly
    ? manufacturers.filter((m) => {
        const n = Number(m.id);
        return !Number.isNaN(n) && favoriteIds.has(n);
      })
    : manufacturers;

  const handleToggleFavorite = (e: React.MouseEvent, manufacturerId: string) => {
    e.stopPropagation();
    const id = Number(manufacturerId);
    if (Number.isNaN(id)) return;
    if (favoriteIds.has(id)) {
      dispatch(updateUser({ favoriteManufacturerIdsToRemove: [id] }));
    } else {
      dispatch(updateUser({ favoriteManufacturerIdsToAdd: [id] }));
    }
  };

  if (isLoading && manufacturers.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-black">
        <LoadingSpinner size={24} color="#B3EC49" />
      </div>
    );
  }

  return (
    <div className="flex-1 bg-black overflow-y-auto p-4 md:p-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {visibleManufacturers.map((manufacturer) => {
          const logoSrc = resolveImageSrc(manufacturer.logo);
          const numericId = Number(manufacturer.id);
          const isFavorite = !Number.isNaN(numericId) && favoriteIds.has(numericId);

          return (
            <div
              key={manufacturer.id}
              role="button"
              tabIndex={0}
              onClick={() => dispatch(selectManufacturer(manufacturer.id))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  dispatch(selectManufacturer(manufacturer.id));
                }
              }}
              className="relative flex flex-col items-center gap-3 p-5 rounded-lg border border-zinc-800
                hover:border-zinc-500 hover:bg-zinc-800/80 hover:scale-[1.03]
                transition-all duration-200 cursor-pointer group"
            >
              {/* Favorite star */}
              <button
                type="button"
                onClick={(e) => handleToggleFavorite(e, manufacturer.id)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/40 hover:bg-black/70 transition-colors z-10"
                title={isFavorite ? t('catalogue.removeFromFavorites') : t('catalogue.addToFavorites')}
                aria-label={isFavorite ? t('catalogue.removeFromFavorites') : t('catalogue.addToFavorites')}
              >
                <Star
                  className={`w-4 h-4 transition-colors ${
                    isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-400 hover:text-yellow-300'
                  }`}
                />
              </button>
              {/* Logo */}
              <div className="w-full h-[80%] rounded-lg bg-zinc-900 flex items-center justify-center overflow-hidden flex-shrink-0">
                {logoSrc ? (
                  <img src={logoSrc} alt={manufacturer.name} className="w-full h-full object-contain" />
                ) : (
                  <span className="w-30 h-30 text-3xl font-bold text-zinc-500 group-hover:text-zinc-400 transition-colors">
                    {manufacturer.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Name */}
              <span
                className="text-md text-gray-300 group-hover:text-white transition-colors text-center truncate w-full"
                title={manufacturer.name}
              >
                {manufacturer.name}
              </span>
            </div>
          );
        })}
      </div>

      {visibleManufacturers.length === 0 && !isLoading && (
        <div className="flex items-center justify-center py-20 text-gray-500 text-sm">
          {favoritesOnly ? t('catalogue.noFavorites') : t('catalogue.noManufacturers')}
        </div>
      )}
    </div>
  );
};

export default ManufacturerGrid;
